import { CloseOutlined, PlayCircleOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Collapse,
  ColorPicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import {
  useLoadZapcapProject,
  useModelTest,
  useProvider,
  useProviderModels,
  useRefreshWatermarkTemplates,
  useRefreshZapcapCatalog,
  useStartModelTest,
  useUploadMedia,
  type WatermarkTemplate,
  type ZapcapChunk,
} from '../../api/hooks';
import type { ProviderModel } from '../../api/types';
import { extractErrorMessage } from '../../api/client';
import { resolveModelParams, UPLOAD_TYPES, type ParamField } from './modelParams';
import { EmojiPicker } from './EmojiPicker';
import { MediaResult } from '../MediaResult';

const { Text } = Typography;

/** Bỏ các giá trị rỗng trước khi gửi. */
function compact(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

function modelLabel(model: ProviderModel): string {
  const cfg = (model.config as Record<string, unknown> | undefined) || {};
  const displayName = typeof cfg.displayName === 'string' ? cfg.displayName.trim() : '';
  return displayName || `${model.name} (${model.code})`;
}

export default function ModelTestTab({ providerId }: { providerId: string }) {
  const { message } = AntApp.useApp();
  const provider = useProvider(providerId);
  const models = useProviderModels(providerId);
  const start = useStartModelTest(providerId);
  const upload = useUploadMedia(providerId);
  const refreshCatalog = useRefreshZapcapCatalog(providerId);
  const loadProject = useLoadZapcapProject(providerId);
  const refreshTemplates = useRefreshWatermarkTemplates(providerId);

  // Zapcap: theme/sound lấy từ provider.config (đã lưu vĩnh viễn qua nút "Cập nhật").
  const providerCfg = (provider.data?.config as Record<string, unknown> | undefined) || {};
  const isZapcap = provider.data?.adapter === 'zapcap-webhook';
  const zapcapThemes = (Array.isArray(providerCfg.themes) ? (providerCfg.themes as string[]) : []).filter(Boolean);
  const SOUND_FALLBACK = ['click', 'ding', 'money', 'pop', 'staple', 'typing', 'whoosh'];
  const zapcapSounds = (Array.isArray(providerCfg.sounds) ? (providerCfg.sounds as string[]) : []).filter(Boolean);
  const soundOptions = zapcapSounds.length ? zapcapSounds : SOUND_FALLBACK;
  // Map slug -> url mp3 (để nghe thử). Ưu tiên url từ /api/v1/sounds, fallback theo quy ước app.zapcap.ai.
  const soundDetails = (Array.isArray(providerCfg.soundDetails)
    ? (providerCfg.soundDetails as { slug: string; url?: string }[])
    : []);
  const soundUrl = (slug: string) =>
    soundDetails.find((s) => s.slug === slug)?.url || `https://app.zapcap.ai/assets/audio/${slug}.mp3`;

  // Watermark: template lấy từ provider.config (đã lưu qua nút "Cập nhật template").
  const isWatermark = provider.data?.adapter === 'watermark-webhook';
  const watermarkTemplates = (Array.isArray(providerCfg.watermarkTemplates)
    ? (providerCfg.watermarkTemplates as WatermarkTemplate[])
    : []
  ).filter((t) => t && (t.name || t.id != null));

  // Editor caption cho luồng zapcap-render: bản gốc (để diff) + bản đang sửa.
  const [projOrig, setProjOrig] = useState<ZapcapChunk[]>([]);
  const [projChunks, setProjChunks] = useState<ZapcapChunk[]>([]);

  const [modelId, setModelId] = useState<string | undefined>();
  const [activeCat, setActiveCat] = useState<string>('__all');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [urlInputs, setUrlInputs] = useState<Record<string, string>>({});
  const [advanced, setAdvanced] = useState('');
  const [testId, setTestId] = useState<string | undefined>();

  const test = useModelTest(testId);
  // File đã ĐÍNH KÈM nhưng CHƯA upload lên nguồn (objectURL -> File).
  // Upload thật chỉ chạy khi bấm "Chạy kiểm thử" (xem onRun).
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [uploading, setUploading] = useState(false);

  const cats = useMemo(() => {
    const set = new Set<string>();
    (models.data ?? []).forEach((m) => set.add(m.category || 'Khác'));
    return Array.from(set);
  }, [models.data]);

  const visibleModels = useMemo(
    () =>
      (models.data ?? []).filter((m) => activeCat === '__all' || (m.category || 'Khác') === activeCat),
    [models.data, activeCat],
  );

  const selected: ProviderModel | undefined = useMemo(
    () => (models.data ?? []).find((m) => m.id === modelId),
    [models.data, modelId],
  );
  const fields: ParamField[] = useMemo(() => (selected ? resolveModelParams(selected) : []), [selected]);
  const isZapcapRender = selected?.code === 'zapcap-render';
  // Ẩn field có điều kiện chưa thỏa (vd PlanX khi mode=klein) — dùng cho cả render lẫn submit.
  const isFieldVisible = (f: ParamField) =>
    !f.showWhen || values[f.showWhen.field] === f.showWhen.equals;
  const visibleFields = useMemo(() => fields.filter(isFieldVisible), [fields, values]);

  const onSelectModel = (id: string) => {
    setModelId(id);
    setTestId(undefined);
    setAdvanced('');
    setUrlInputs({});
    setProjOrig([]);
    setProjChunks([]);
    const m = (models.data ?? []).find((x) => x.id === id);
    const init: Record<string, unknown> = {};
    if (m)
      for (const f of resolveModelParams(m)) {
        if (f.default !== undefined) init[f.name] = f.default;
        if (f.type === 'image[]' || f.type === 'video[]') init[f.name] = (init[f.name] as unknown[]) ?? [];
      }
    setValues(init);
  };

  const setValue = (name: string, v: unknown) => setValues((prev) => ({ ...prev, [name]: v }));

  // Gắn 1 URL (đã upload hoặc dán tay) vào field — append nếu là mảng, gán nếu đơn.
  const appendUrl = (field: ParamField, url: string) =>
    setValues((prev) => {
      if (field.type === 'image[]' || field.type === 'video[]') {
        const arr = Array.isArray(prev[field.name]) ? (prev[field.name] as string[]) : [];
        return { ...prev, [field.name]: [...arr, url] };
      }
      return { ...prev, [field.name]: url };
    });

  // ĐÍNH KÈM file: CHỈ chuẩn bị cục bộ (objectURL để xem trước), KHÔNG upload lên
  // nguồn. Việc upload thật chạy khi bấm "Chạy kiểm thử" (onRun) — đúng bản chất
  // "chuẩn bị file", nên thao tác đính kèm luôn tức thì và không thể lỗi mạng.
  const stageFile = (field: ParamField, file: File) => {
    const localUrl = URL.createObjectURL(file);
    setPendingFiles((p) => ({ ...p, [localUrl]: file }));
    appendUrl(field, localUrl);
  };

  // Thêm ảnh/video bằng URL có sẵn (giống cách phần mềm thứ 3 gửi URL ảnh).
  const addUrlManually = (field: ParamField) => {
    const url = (urlInputs[field.name] || '').trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      message.warning('URL phải bắt đầu bằng http:// hoặc https://');
      return;
    }
    appendUrl(field, url);
    setUrlInputs((p) => ({ ...p, [field.name]: '' }));
  };

  const removeUrl = (field: ParamField, url: string) => {
    if (pendingFiles[url]) {
      URL.revokeObjectURL(url);
      setPendingFiles((p) => {
        const n = { ...p };
        delete n[url];
        return n;
      });
    }
    setValues((prev) => {
      if (field.type === 'image[]' || field.type === 'video[]') {
        const arr = (prev[field.name] as string[]) ?? [];
        return { ...prev, [field.name]: arr.filter((u) => u !== url) };
      }
      return { ...prev, [field.name]: undefined };
    });
  };

  // Zapcap: cập nhật theme + sound từ nguồn (lưu vĩnh viễn vào provider.config).
  const onRefreshCatalog = async () => {
    try {
      const r = await refreshCatalog.mutateAsync();
      await provider.refetch();
      message.success(`Đã cập nhật ${r.themes.length} theme, ${r.sounds.length} âm thanh từ nguồn`);
    } catch (e) {
      message.error(extractErrorMessage(e, 'Cập nhật theme/âm thanh thất bại'));
    }
  };

  // Watermark: cập nhật danh sách template từ nguồn (lưu vào provider.config).
  const onRefreshTemplates = async () => {
    try {
      const r = await refreshTemplates.mutateAsync();
      await provider.refetch();
      message.success(`Đã cập nhật ${r.templates.length} template từ nguồn`);
    } catch (e) {
      message.error(extractErrorMessage(e, 'Cập nhật template thất bại'));
    }
  };

  // Zapcap render: nạp thông số gốc + toàn bộ caption của project cũ để hiển thị và sửa.
  const onLoadProject = async () => {
    const pid = String(values['project_id'] ?? '').trim();
    if (!pid) {
      message.warning('Nhập Project ID trước');
      return;
    }
    try {
      const data = await loadProject.mutateAsync(pid);
      const o = (data.options || {}) as Record<string, unknown>;
      // Đổ thông số gốc vào form (giữ giá trị người dùng đã nhập nếu nguồn không trả).
      setValues((prev) => ({
        ...prev,
        theme: o.theme ?? prev.theme,
        color: o.color ?? prev.color,
        size: o.size ?? prev.size,
        position_y: o.position_y ?? prev.position_y,
        highlight_main: o.highlight_main ?? prev.highlight_main,
        highlight_second: o.highlight_second ?? prev.highlight_second,
        highlight_third: o.highlight_third ?? prev.highlight_third,
      }));
      const norm: ZapcapChunk[] = (data.chunks || []).map((c) => ({
        index: c.index,
        emoji: c.emoji || '',
        sound: c.sound || '',
        sound_volume: c.sound_volume || '',
        widgets: (c.widgets || []).map((w) => ({
          text: w.text || '',
          emoji: w.emoji || '',
          important: !!w.important,
        })),
      }));
      setProjOrig(JSON.parse(JSON.stringify(norm)));
      setProjChunks(JSON.parse(JSON.stringify(norm)));
      message.success(`Đã nạp ${norm.length} đoạn caption của dự án`);
    } catch (e) {
      message.error(extractErrorMessage(e, 'Nạp dự án thất bại'));
    }
  };

  const setChunkField = (ci: number, key: 'emoji' | 'sound' | 'sound_volume', val: string) =>
    setProjChunks((prev) => prev.map((c, i) => (i === ci ? { ...c, [key]: val } : c)));
  const setWidgetText = (ci: number, wi: number, val: string) =>
    setProjChunks((prev) =>
      prev.map((c, i) =>
        i === ci ? { ...c, widgets: c.widgets.map((w, j) => (j === wi ? { ...w, text: val } : w)) } : c,
      ),
    );

  // Diff bản đang sửa với bản gốc -> widget_overrides + chunk_overrides (bỏ field = giữ nguyên).
  const buildRenderOverrides = () => {
    const widget_overrides: Record<string, unknown>[] = [];
    const chunk_overrides: Record<string, unknown>[] = [];
    projChunks.forEach((c, ci) => {
      const o = projOrig[ci];
      const cc: Record<string, unknown> = {};
      if (o) {
        if (c.emoji !== o.emoji) cc.emoji = c.emoji;
        if (c.sound !== o.sound) cc.sound = c.sound;
        if (c.sound_volume !== o.sound_volume) cc.sound_volume = c.sound_volume;
      }
      if (Object.keys(cc).length) chunk_overrides.push({ chunk_index: c.index, ...cc });
      c.widgets.forEach((w, wi) => {
        const ow = o?.widgets[wi];
        // Chỉ override TEXT của từng từ. Emoji theo từ bị bỏ (không áp dụng) — chỉ emoji của ĐOẠN.
        if (ow && w.text !== ow.text) widget_overrides.push({ chunk_index: c.index, widget_index: wi, text: w.text });
      });
    });
    return { widget_overrides, chunk_overrides };
  };

  const onRun = async () => {
    if (!modelId) {
      message.warning('Chọn model để kiểm thử');
      return;
    }
    let extra: Record<string, unknown> = {};
    if (advanced.trim()) {
      try {
        extra = JSON.parse(advanced);
      } catch {
        message.error('Tham số nâng cao (JSON) không hợp lệ');
        return;
      }
    }

    // BƯỚC 1 — upload các file đã đính kèm (objectURL) lên nguồn để lấy URL thật.
    // Chỉ chạy ở đây (lúc bấm kiểm thử), KHÔNG chạy lúc đính kèm.
    const resolved: Record<string, unknown> = { ...values };
    if (Object.keys(pendingFiles).length) {
      setUploading(true);
      try {
        for (const f of fields) {
          if (!UPLOAD_TYPES.includes(f.type)) continue;
          const raw = resolved[f.name];
          const list = Array.isArray(raw) ? (raw as string[]) : raw ? [raw as string] : [];
          const out: string[] = [];
          for (const ref of list) {
            const file = pendingFiles[ref];
            if (file) {
              const fd = new FormData();
              // mediaType: audio/video/image. Với 'media' suy theo MIME của file thật.
              const mediaType =
                f.type === 'audio'
                  ? 'audio'
                  : f.type === 'media'
                    ? file.type.startsWith('video')
                      ? 'video'
                      : file.type.startsWith('audio')
                        ? 'audio'
                        : 'image'
                    : f.type.startsWith('video')
                      ? 'video'
                      : 'image';
              fd.append('mediaType', mediaType);
              // modelId: để config model ghi đè config provider (vd Dola video upload qua
              // /api/v1/videos/upload-reference và trả `key` thay vì preview_url).
              if (modelId) fd.append('modelId', modelId);
              fd.append('file', file);
              const res = await upload.mutateAsync(fd);
              if (!res.url) throw new Error('Upload ảnh tham chiếu thất bại');
              out.push(res.url);
            } else out.push(ref);
          }
          resolved[f.name] = f.type.endsWith('[]') ? out : out[0];
        }
      } catch (e) {
        setUploading(false);
        message.error(extractErrorMessage(e, 'Tải ảnh tham chiếu lên nguồn thất bại'));
        return;
      }
      setUploading(false);
      // Đồng bộ lại UI + thu hồi objectURL để lần chạy sau không upload trùng.
      Object.keys(pendingFiles).forEach((u) => URL.revokeObjectURL(u));
      setPendingFiles({});
      setValues(resolved);
    }

    // Bỏ giá trị của các field đang ẨN (vd PlanX khi mode=klein) để không gửi thừa lên nguồn.
    for (const f of fields) if (!isFieldVisible(f)) delete resolved[f.name];

    // Field kiểu 'json' (vd Zapcap text_edits/word_emojis/widget_overrides/chunk_overrides):
    // người dùng nhập chuỗi JSON -> parse thành mảng/đối tượng trước khi gửi.
    for (const f of fields) {
      if (f.type !== 'json') continue;
      const raw = resolved[f.name];
      if (typeof raw !== 'string' || !raw.trim()) {
        delete resolved[f.name];
        continue;
      }
      try {
        resolved[f.name] = JSON.parse(raw);
      } catch {
        message.error(`Trường "${f.label}" không phải JSON hợp lệ`);
        return;
      }
    }

    // Zapcap render: build widget_overrides/chunk_overrides từ editor caption (diff vs gốc).
    if (isZapcapRender && projChunks.length) {
      const { widget_overrides, chunk_overrides } = buildRenderOverrides();
      if (widget_overrides.length) resolved.widget_overrides = widget_overrides;
      if (chunk_overrides.length) resolved.chunk_overrides = chunk_overrides;
    }

    // BƯỚC 2 — gửi yêu cầu kiểm thử với input đã có URL thật.
    const input = { ...compact(resolved), ...extra };
    try {
      const { testId: id } = await start.mutateAsync({ modelId, input });
      setTestId(id);
      message.success('Đã gửi yêu cầu kiểm thử');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const renderPreview = (field: ParamField, url: string) => {
    const src = url; // objectURL (chưa upload) hoặc URL thật (đã upload) đều hiển thị được
    // Với type 'media' (ảnh/video/audio) suy ra cách hiển thị theo đuôi URL.
    const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
    const isAudio = field.type === 'audio' || (field.type === 'media' && ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'opus'].includes(ext));
    const isVideo =
      field.type.startsWith('video') ||
      (field.type === 'media' && ['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv'].includes(ext));
    return (
    <div key={url} style={{ position: 'relative', display: 'inline-block' }}>
      {isAudio ? (
        <audio src={src} controls style={{ width: 220, verticalAlign: 'middle' }} />
      ) : isVideo ? (
        <video
          src={src}
          preload="metadata"
          muted
          style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
        />
      ) : (
        <img src={src} alt="" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }} />
      )}
      <Button
        size="small"
        shape="circle"
        icon={<CloseOutlined />}
        onClick={() => removeUrl(field, url)}
        style={{ position: 'absolute', top: -8, right: -8, boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}
      />
    </div>
    );
  };

  const renderUploadField = (field: ParamField) => {
    const isArray = field.type.endsWith('[]');
    const accept =
      field.type === 'audio'
        ? 'audio/*'
        : field.type === 'media'
          ? 'image/*,video/*,audio/*'
          : field.type.startsWith('video')
            ? 'video/*'
            : 'image/*';
    const current = isArray
      ? ((values[field.name] as string[]) ?? [])
      : values[field.name]
        ? [values[field.name] as string]
        : [];
    return (
      <Form.Item key={field.name} label={field.label} required={field.required}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* ĐÍNH KÈM (chuẩn bị file) — chưa upload lên nguồn. Hỗ trợ: nút chọn file
              native, kéo-thả, dán ảnh, dán URL. Upload thật chạy khi bấm "Chạy kiểm thử". */}
          <div
            tabIndex={0}
            onPaste={(e) => {
              const files = Array.from(e.clipboardData?.files ?? []);
              if (files.length) {
                e.preventDefault();
                files.forEach((f) => stageFile(field, f));
              }
            }}
            style={{ outline: 'none' }}
          >
            <Upload.Dragger
              accept={accept}
              multiple={isArray}
              showUploadList={false}
              beforeUpload={(file) => {
                stageFile(field, file);
                return Upload.LIST_IGNORE;
              }}
              style={{ padding: '10px 14px' }}
            >
              <p style={{ margin: 0, fontSize: 14 }}>
                <UploadOutlined /> Kéo-thả {field.type === 'audio' ? 'audio' : field.type === 'media' ? 'ảnh/video/audio' : field.type.startsWith('video') ? 'ảnh/video' : 'ảnh'} vào đây, hoặc bấm để chọn
                {field.max ? ` · tối đa ${field.max}` : ''}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8c8c8c' }}>
                Bấm để chọn file. Nếu hộp thoại không mở: <b>kéo-thả</b> file vào đây, hoặc copy ảnh rồi{' '}
                <b>dán (Cmd/Ctrl+V)</b>, hoặc dùng nút native ngay dưới / dán URL.
              </p>
            </Upload.Dragger>
          </div>
          {/* Nút chọn file NATIVE thuần của trình duyệt (người dùng bấm trực tiếp vào
              control gốc) — phương án mở hộp thoại đáng tin cậy nhất nếu Dragger không mở. */}
          <label style={{ fontSize: 12, color: '#595959', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Hoặc dùng nút gốc của trình duyệt:
            <input
              type="file"
              accept={accept}
              multiple={isArray}
              onChange={(e) => {
                Array.from(e.target.files ?? []).forEach((f) => stageFile(field, f));
                e.target.value = '';
              }}
            />
          </label>
          <Space wrap>
            {upload.isPending && <Spin size="small" />}
            <Space.Compact>
              <Input
                style={{ width: 280 }}
                placeholder="hoặc dán URL ảnh/video"
                value={urlInputs[field.name] || ''}
                onChange={(e) => setUrlInputs((p) => ({ ...p, [field.name]: e.target.value }))}
                onPressEnter={() => addUrlManually(field)}
              />
              <Button onClick={() => addUrlManually(field)}>Thêm URL</Button>
            </Space.Compact>
          </Space>
          {current.length > 0 && <Space wrap>{current.map((u) => renderPreview(field, u))}</Space>}
        </Space>
      </Form.Item>
    );
  };

  const renderField = (field: ParamField) => {
    if (UPLOAD_TYPES.includes(field.type)) return renderUploadField(field);
    const v = values[field.name];
    switch (field.type) {
      case 'textarea':
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <Input.TextArea
              rows={3}
              value={v as string}
              onChange={(e) => setValue(field.name, e.target.value)}
            />
          </Form.Item>
        );
      case 'number':
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <InputNumber
              style={{ width: '100%' }}
              value={v as number}
              onChange={(val) => setValue(field.name, val)}
            />
          </Form.Item>
        );
      case 'bool':
        return (
          <Form.Item key={field.name} label={field.label}>
            <Switch checked={!!v} onChange={(c) => setValue(field.name, c)} />
          </Form.Item>
        );
      case 'json':
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <Input.TextArea
              rows={3}
              placeholder='vd: [{"find":"abc","replace":"xyz"}]'
              value={v as string}
              onChange={(e) => setValue(field.name, e.target.value)}
            />
          </Form.Item>
        );
      case 'select': {
        // Theme của Zapcap lấy danh sách động từ provider.config (nếu đã cập nhật).
        const opts =
          isZapcap && field.name === 'theme' && zapcapThemes.length ? zapcapThemes : field.options ?? [];
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <Select
              style={{ width: '100%' }}
              allowClear
              showSearch
              placeholder="Chọn giá trị"
              value={v as string | number | undefined}
              onChange={(val) => setValue(field.name, val)}
              options={opts.map((o) => ({ value: o, label: String(o) }))}
            />
          </Form.Item>
        );
      }
      case 'color':
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <ColorPicker
              format="hex"
              allowClear
              value={(v as string) || undefined}
              // Lấy hex từ chính đối tượng màu (chuẩn #RRGGBB, bỏ alpha nếu có) rồi LƯU vào values
              // -> đảm bảo color/highlight_* được gửi lên API gốc.
              onChange={(c) => {
                let hex = c ? c.toHexString() : '';
                if (hex.length === 9) hex = hex.slice(0, 7); // #RRGGBBAA -> #RRGGBB
                setValue(field.name, hex.toUpperCase());
              }}
              onClear={() => setValue(field.name, '')}
              showText
            />
          </Form.Item>
        );
      default:
        // Watermark: field `template` chọn từ danh sách nguồn (vẫn cho tự nhập id/tên).
        if (isWatermark && field.name === 'template') {
          return (
            <Form.Item
              key={field.name}
              label={field.label}
              required={field.required}
              extra={
                watermarkTemplates.length
                  ? undefined
                  : 'Chưa có danh sách — bấm "Cập nhật template" ở trên, hoặc nhập id/tên template.'
              }
            >
              <AutoComplete
                allowClear
                style={{ width: '100%' }}
                value={v as string}
                onChange={(val) => setValue(field.name, val)}
                placeholder="Chọn template hoặc nhập id/tên"
                filterOption={(input, opt) =>
                  String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={watermarkTemplates.map((t) => ({
                  value: t.name || String(t.id),
                  label: `${t.name || t.id}${t.description ? ` — ${t.description}` : ''}`,
                }))}
              />
            </Form.Item>
          );
        }
        return (
          <Form.Item key={field.name} label={field.label} required={field.required}>
            <Input value={v as string} onChange={(e) => setValue(field.name, e.target.value)} />
          </Form.Item>
        );
    }
  };

  // Các kiểu chiếm trọn hàng (prompt, textarea, vùng tải ảnh/video). Còn lại
  // (number/select/bool/text/url…) gộp 2–3 thông số trên cùng một hàng.
  const isFullWidth = (f: ParamField) =>
    f.type === 'textarea' || f.type === 'json' || UPLOAD_TYPES.includes(f.type);

  const renderFieldGrid = (list: ParamField[]) => {
    const nodes: ReactNode[] = [];
    let bucket: ParamField[] = [];
    const flush = () => {
      if (!bucket.length) return;
      nodes.push(
        <Row gutter={16} key={`grid-${nodes.length}`}>
          {bucket.map((f) => (
            <Col xs={24} sm={12} lg={8} key={f.name}>
              {renderField(f)}
            </Col>
          ))}
        </Row>,
      );
      bucket = [];
    };
    for (const f of list) {
      if (isFullWidth(f)) {
        flush();
        nodes.push(<div key={f.name}>{renderField(f)}</div>);
      } else {
        bucket.push(f);
      }
    }
    flush();
    return nodes;
  };

  // Khối nạp + sửa caption cho luồng zapcap-render.
  const zapcapEditor = isZapcapRender ? (
    <>
      <Row gutter={16} align="bottom" style={{ marginBottom: 8 }}>
        <Col flex="auto">
          <Form.Item label="Project ID" required style={{ marginBottom: 0 }}>
            <Input
              placeholder="Dán project_id từ kết quả job caption"
              value={(values['project_id'] as string) ?? ''}
              onChange={(e) => setValue('project_id', e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col>
          <Button onClick={onLoadProject} loading={loadProject.isPending}>
            Load dự án cũ
          </Button>
        </Col>
      </Row>
      {loadProject.isPending && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }} message="Đang nạp dự án… (đọc caption có thể mất tới ~90s)" />
      )}
      {projChunks.length > 0 && (
        <Card size="small" title={`Caption dự án (${projChunks.length} đoạn) — sửa rồi bấm Chạy kiểm thử`} style={{ marginBottom: 12 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Icon (emoji) là chuỗi Unicode bất kỳ — chọn từ danh sách hoặc gõ tay; để trống = xoá. Âm thanh chọn theo slug, bấm nghe thử bên dưới. Emoji chỉ áp dụng cho cả ĐOẠN, không theo từng từ."
          />
          <Collapse
            ghost
            items={projChunks.map((c, ci) => ({
              key: String(c.index),
              label: (
                <span>
                  Đoạn {c.index}
                  {c.sound ? <Tag color="blue" style={{ marginLeft: 8 }}>🔊 {c.sound}</Tag> : null}
                  {c.emoji ? <Tag style={{ marginLeft: 4 }}>{c.emoji}</Tag> : null}
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {c.widgets.map((w) => w.text).join(' ')}
                  </Text>
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Row gutter={12}>
                    <Col xs={24} sm={8}>
                      <Form.Item label="Icon đoạn (emoji)" style={{ marginBottom: 8 }}>
                        <EmojiPicker value={c.emoji} onChange={(val) => setChunkField(ci, 'emoji', val)} placeholder="rỗng = xoá" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item label="Âm thanh (sound)" style={{ marginBottom: 8 }}>
                        <Select
                          allowClear
                          showSearch
                          style={{ width: '100%' }}
                          placeholder="(không)"
                          value={c.sound || undefined}
                          onChange={(val) => setChunkField(ci, 'sound', val ?? '')}
                          options={soundOptions.map((s) => ({ value: s, label: s }))}
                        />
                        {c.sound ? (
                          <audio src={soundUrl(c.sound)} controls preload="none" style={{ width: '100%', height: 32, marginTop: 6 }} />
                        ) : null}
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item label="Âm lượng (0.0–1.0)" style={{ marginBottom: 8 }}>
                        <Input value={c.sound_volume} onChange={(e) => setChunkField(ci, 'sound_volume', e.target.value)} placeholder="vd: 0.8" />
                      </Form.Item>
                    </Col>
                  </Row>
                  {c.widgets.map((w, wi) => (
                    <Form.Item key={wi} label={wi === 0 ? 'Caption (text từng từ)' : undefined} style={{ marginBottom: 8 }}>
                      <Input
                        value={w.text}
                        onChange={(e) => setWidgetText(ci, wi, e.target.value)}
                        addonAfter={w.important ? <Tag color="gold" style={{ margin: 0 }}>highlight</Tag> : undefined}
                      />
                    </Form.Item>
                  ))}
                </Space>
              ),
            }))}
          />
        </Card>
      )}
    </>
  ) : null;

  const result = test.data;

  // Nhật ký job (gồm: giải "Auto" aspect_ratio + REQUEST gửi lên API gốc) để debug.
  const logs = result?.logs ?? [];
  const logTag = (type: string) => {
    const map: Record<string, { color: string; label: string }> = {
      request: { color: 'purple', label: 'REQUEST → API gốc' },
      aspect_ratio: { color: 'cyan', label: 'Auto aspect_ratio' },
      created: { color: 'default', label: 'Tạo job' },
      completed: { color: 'success', label: 'Hoàn tất' },
      failed: { color: 'error', label: 'Thất bại' },
    };
    const m = map[type] || { color: 'blue', label: type };
    return <Tag color={m.color}>{m.label}</Tag>;
  };
  const logsBody = logs.length > 0 && (
    <Collapse
      ghost
      defaultActiveKey={['logs']}
      items={[
        {
          key: 'logs',
          label: `Nhật ký gửi request (${logs.length})`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {logs.map((lg, i) => (
                <div key={i}>
                  <Space size="small" wrap>
                    {logTag(lg.type)}
                    <Text style={{ fontSize: 13 }}>{lg.message}</Text>
                  </Space>
                  {lg.data != null && (
                    <pre
                      style={{
                        background: '#0f172a',
                        color: '#e2e8f0',
                        padding: 10,
                        borderRadius: 6,
                        marginTop: 4,
                        maxHeight: 260,
                        overflow: 'auto',
                        fontSize: 12,
                      }}
                    >
                      {JSON.stringify(lg.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </Space>
          ),
        },
      ]}
    />
  );

  const resultBody = (
    <Card title="Kết quả kiểm thử" style={{ position: 'sticky', top: 8 }}>
      {!testId && <Empty description="Chưa chạy kiểm thử" />}
      {testId && test.isLoading && <Spin />}
      {testId && result && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            Trạng thái:{' '}
            {result.status === 'running' && <Tag color="processing">{result.note || 'Đang chạy…'}</Tag>}
            {result.status === 'succeeded' && <Tag color="success">Thành công</Tag>}
            {result.status === 'failed' && <Tag color="error">Thất bại</Tag>}
            {result.runner && (
              <Tag color={result.runner === 'worker' ? 'geekblue' : 'default'}>
                {result.runner === 'worker' ? 'Chạy bởi Worker' : 'Chạy trên Gateway'}
              </Tag>
            )}
          </div>

          {result.status === 'running' && <Spin />}

          {logsBody}

          {result.status === 'failed' && result.error && (
            <Alert type="error" message={`Lỗi (${result.error.class ?? 'fatal'})`} description={result.error.message} />
          )}

          {result.status === 'succeeded' && <MediaResult output={result.output} />}

          {result.status === 'succeeded' && (
            <Collapse
              ghost
              items={[
                {
                  key: 'raw-output',
                  label: 'Output (JSON thô)',
                  children: (
                    <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {JSON.stringify(result.output, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          )}
        </Space>
      )}
    </Card>
  );

  return (
    <div>
      <Alert
        type="info"
        message="Kiểm thử model bằng job thật qua hàng đợi: model nào có worker sẽ chạy bởi worker như vận hành thực tế; Gateway chỉ fallback khi không có worker online hoặc nguồn bắt buộc chạy Gateway."
        style={{ marginBottom: 16 }}
      />
      <Row gutter={24}>
        {/* CỘT TRÁI: công cụ nhập liệu */}
        <Col xs={24} lg={13}>
          {cats.length > 1 && (
            <Tabs
              activeKey={activeCat}
              onChange={(k) => setActiveCat(k)}
              items={[{ key: '__all', label: 'Tất cả' }, ...cats.map((c) => ({ key: c, label: c }))]}
            />
          )}
          <Form layout="vertical">
            <Form.Item label="Model" required>
              <Space wrap>
                <Select
                  style={{ width: 340 }}
                  placeholder="Chọn model để kiểm thử"
                  value={modelId}
                  onChange={onSelectModel}
                  loading={models.isLoading}
                  showSearch
                  optionFilterProp="label"
                  options={visibleModels.map((m) => ({ value: m.id, label: modelLabel(m) }))}
                />
                <Button icon={<ReloadOutlined />} loading={models.isFetching} onClick={() => models.refetch()}>
                  Load model
                </Button>
                {isZapcap && (
                  <Button icon={<ReloadOutlined />} loading={refreshCatalog.isPending} onClick={onRefreshCatalog}>
                    Cập nhật theme/âm thanh
                  </Button>
                )}
                {isWatermark && (
                  <Button icon={<ReloadOutlined />} loading={refreshTemplates.isPending} onClick={onRefreshTemplates}>
                    Cập nhật template
                  </Button>
                )}
              </Space>
            </Form.Item>

            {selected && (
              <>
                {zapcapEditor}
                {renderFieldGrid(isZapcapRender ? visibleFields.filter((f) => f.name !== 'project_id') : visibleFields)}

                <Collapse
                  ghost
                  style={{ marginBottom: 12 }}
                  items={[
                    {
                      key: 'adv',
                      label: 'Tham số nâng cao (JSON) — gộp đè lên các giá trị ở trên',
                      children: (
                        <Input.TextArea
                          rows={4}
                          value={advanced}
                          onChange={(e) => setAdvanced(e.target.value)}
                          style={{ fontFamily: 'monospace' }}
                          placeholder='{ "seed": 123 }'
                        />
                      ),
                    },
                  ]}
                />

                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={uploading || start.isPending}
                  onClick={onRun}
                >
                  {uploading ? 'Đang tải ảnh lên nguồn…' : 'Chạy kiểm thử'}
                </Button>
              </>
            )}
          </Form>
        </Col>

        {/* CỘT PHẢI: kết quả kiểm thử */}
        <Col xs={24} lg={11}>
          {resultBody}
        </Col>
      </Row>
    </div>
  );
}
